import { ReactNode, useEffect, useMemo, useState } from 'react';

import { KasePropertyApi } from '@/api';
import { CFSProvider, KaseProvider } from '@/contexts';
import {
  PropertyContextProvider,
  PropertySourceAdapter,
} from '@/features/properties';
import { useKaseContext } from '@/hooks';
import { KaseMainContextProvider } from '@/pages/Kases/KaseEdit/KaseMainContext';

import PropertyEvidenceDataGrid from './PropertyEvidenceDataGrid';

interface EvidenceContextBridgeProps {
  currentParentId: string;
  children: ReactNode;
}

const EvidenceContextBridge = ({
  currentParentId,
  children,
}: EvidenceContextBridgeProps) => {
  const { kase, fetchKase } = useKaseContext();

  useEffect(() => {
    if (currentParentId) {
      fetchKase(currentParentId);
    }
  }, [currentParentId]);

  return <CFSProvider cfsId={kase?.cfsParent}>{children}</CFSProvider>;
};

const PropertyEvidencePage = () => {
  const [currentParentId, setCurrentParentId] = useState<string>('');

  const sourceAdapter = useMemo<PropertySourceAdapter>(
    () => ({
      source: 'kase',
      parentId: currentParentId,
      routeBase: '',
      list: KasePropertyApi.list,
      paginatedList: KasePropertyApi.paginatedList,
      getOne: KasePropertyApi.getOne,
      save: KasePropertyApi.save,
      delete: KasePropertyApi.delete,
      getCustodyLogs: KasePropertyApi.getCustodyLogs,
      custodyCheckIn: KasePropertyApi.custodyCheckIn,
      custodyCheckoutLab: KasePropertyApi.custodyCheckoutLab,
      custodyCheckoutCourt: KasePropertyApi.custodyCheckoutCourt,
      custodyCheckoutPerson: KasePropertyApi.custodyCheckoutPerson,
      custodyDispose: KasePropertyApi.custodyDispose,
      custodyMoveLocation: KasePropertyApi.custodyMoveLocation,
      custodyReleaseToOwner: KasePropertyApi.custodyReleaseToOwner,
      custodyDestroy: KasePropertyApi.custodyDestroy,
      custodyAuction: KasePropertyApi.custodyAuction,
      custodyTransfer: KasePropertyApi.custodyTransfer,
    }),
    [currentParentId],
  );

  return (
    <KaseMainContextProvider>
      <KaseProvider>
        <EvidenceContextBridge currentParentId={currentParentId}>
          <PropertyContextProvider
            nextRoutes={[]}
            sourceAdapter={sourceAdapter}
          >
            <PropertyEvidenceDataGrid onSelectKaseId={setCurrentParentId} />
          </PropertyContextProvider>
        </EvidenceContextBridge>
      </KaseProvider>
    </KaseMainContextProvider>
  );
};

export default PropertyEvidencePage;
